import { Color } from '@diritto/common/style/color';
import { Typography } from '@diritto/common/style/typography';
import { css, SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';
import { isPC } from 'constants/layout';
import { ZIndex } from 'constants/zIndex';
import { ArrowIcon } from 'icons/ArrowIcon';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { getTextLineStyle } from 'styles/common';
import { useMenuRef } from 'ui-hooks/useMenuRef';
import { useSetCenterScroll } from 'ui-hooks/useSetCenterScroll';

type SelectItemType<S> = { key: S; value: string; style?: SerializedStyles };

type ListPosition = 'top' | 'bottom' | 'center';

export interface SelectBoxProps<S extends string> {
  id?: string;
  className?: string;
  defaultValue?: S;
  selectList: SelectItemType<S>[];
  value?: S;
  showCount?: number;
  listBackgroundColor?: string;
  listPosition?: ListPosition;
  onChange: (select: S) => void;
  placeholder?: string;
}

export const SelectBoxItemHeight = 48;
const MaxShowCount = 5;

export const SelectBox = <S extends string>({
  id,
  className,
  defaultValue,
  value,
  selectList,
  showCount = MaxShowCount,
  listBackgroundColor = 'transparent',
  listPosition = 'bottom',
  placeholder,
  onChange,
}: SelectBoxProps<S>) => {
  const { ref, isOpen, setOpen } = useMenuRef<HTMLDivElement>();
  const [selectedKey, setSelectedKey] = useState(defaultValue || value || selectList[0].key);
  const [showPlaceholder, setShowPlaceholder] = useState(!!placeholder);
  const { selectedValue, selectedStyle } = useMemo(() => {
    const selectedItem = selectList.find(item => item.key === selectedKey) || selectList[0];

    return {
      selectedValue: selectedItem.value,
      selectedStyle: selectedItem.style,
    };
  }, [selectedKey, selectList]);

  const { itemRefs, setScrollCenter } = useSetCenterScroll(selectList);

  const toggleShowList = useCallback(() => setOpen(prev => !prev), [setOpen]);

  const handleSelect = useCallback(
    (item: S) => {
      setOpen(false);
      setSelectedKey(item);
      onChange?.(item);
    },
    [onChange, setOpen],
  );

  const handleChangeSelect = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      handleSelect(e.target.value as S);
    },
    [handleSelect],
  );

  useEffect(() => {
    if (isOpen && selectedKey) {
      setScrollCenter(selectedKey);
    }
  }, [isOpen, selectedKey, setScrollCenter]);

  useEffect(() => {
    if (value) {
      setSelectedKey(value);
      setShowPlaceholder(false);
    }
  }, [value]);

  return (
    <StyledContainer id={id} ref={ref} className={className}>
      <StyledButton onClick={toggleShowList}>
        {showPlaceholder ? (
          <StyledPlaceholder serializedStyle={selectedStyle}>{placeholder}</StyledPlaceholder>
        ) : (
          <StyledTitle serializedStyle={selectedStyle}>{selectedValue}</StyledTitle>
        )}
        <ArrowIcon className="size-[20px]" direction={listPosition === 'top' ? 'up' : 'down'} color={Color.Gray400} />
      </StyledButton>
      {/* Only PC */}
      {isOpen && (
        <StyledList showCount={showCount} backgroundColor={listBackgroundColor} listPosition={listPosition}>
          {selectList.map(({ key, value: item, style }) => (
            <li key={key} ref={itemRefs[key]}>
              <StyledListItem
                serializedStyle={style}
                isSelected={key === selectedKey}
                onClick={() => handleSelect(key)}>
                {item}
              </StyledListItem>
            </li>
          ))}
        </StyledList>
      )}
      {/* Only Mobile */}
      <StyledSelect onChange={handleChangeSelect} defaultValue={defaultValue} value={value}>
        {selectList.map(({ key, value: item }) => (
          <option key={key} value={key}>
            {item}
          </option>
        ))}
      </StyledSelect>
    </StyledContainer>
  );
};

const getListPositionStyle = (listPosition: ListPosition) => {
  if (listPosition === 'top') {
    return css`
      bottom: calc(100% + 2px);
    `;
  }
  if (listPosition === 'bottom') {
    return css`
      top: calc(100% + 2px);
    `;
  }

  return css`
    top: 50%;
    transform: translateY(-50%);
  `;
};

const getListItemStyle = () => css`
  padding: 0 16px;
  width: 100%;
  height: ${SelectBoxItemHeight}px;

  ${Typography.XXSmall};

  display: flex;
  align-items: center;
  gap: 16px;

  border: none;
  cursor: pointer;
`;

const StyledContainer = styled.div`
  display: flex;
  width: 100%;
  height: 48px;
  position: relative;
`;

const StyledPlaceholder = styled.p(
  ({ serializedStyle }: { serializedStyle?: SerializedStyles }) => css`
    ${getTextLineStyle(1)}
    color: ${Color.Black};
    text-align: left;
    ${serializedStyle};
  `,
);

const StyledTitle = styled.p(
  ({ serializedStyle }: { serializedStyle?: SerializedStyles }) => css`
    ${getTextLineStyle(1)}

    color: ${Color.Black};
    text-align: left;
    ${serializedStyle};
  `,
);

const StyledList = styled.ul(
  ({
    showCount,
    backgroundColor,
    listPosition,
  }: {
    showCount: number;
    backgroundColor: string;
    listPosition: ListPosition;
  }) => css`
    display: none;
    width: 100%;
    max-height: ${SelectBoxItemHeight * showCount}px;
    position: absolute;
    right: 0;
    z-index: ${ZIndex.ViewerOptionFontSelector};
    overflow-y: auto;
    overscroll-behavior-y: contain;
    border-radius: 4px;
    box-shadow:
      0 2px 4px -1px rgba(0, 0, 0, 0.2),
      0px 8px 16px -1px rgba(0, 0, 0, 0.2);
    margin: 0;
    padding: 8px;
    background-color: ${backgroundColor};

    & li {
      margin: 0;
      padding: 0;

      &::marker {
        content: '';
      }
    }

    @media (${isPC}) {
      display: initial;
    }

    ${getListPositionStyle(listPosition)}
  `,
);

const StyledButton = styled.button`
  position: absolute;
  top: 0;
  left: 0;

  ${getListItemStyle()};

  display: flex;
  flex-direction: row;

  justify-content: space-between;

  border-radius: 4px;
  border: 1px solid ${Color.Gray300};

  background-color: ${Color.White};

  @media (${isPC}) {
    position: initial;
    top: initial;
    left: initial;
  }
`;

const StyledListItem = styled.button(
  ({ serializedStyle, isSelected }: { serializedStyle?: SerializedStyles; isSelected: boolean }) => css`
    ${getListItemStyle()}

    justify-content: flex-start;

    background-color: ${isSelected ? `${Color.Gray100} !important` : 'initial'};

    ${serializedStyle};

    &:hover {
      background-color: ${Color.Gray100};
    }
  `,
);

const StyledSelect = styled.select`
  width: 100%;
  height: 100%;
  opacity: 0;

  @media (${isPC}) {
    display: none;
  }
`;
